import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import { NAV_MENU_BREAKPOINT } from '@constants';
import { AlgoliaHit, NavStatus } from '@contracts';
import { NavContext } from './Context';

interface Props {
  hits: AlgoliaHit[];
}

function NavSearchProductHits({ hits }: Props) {
  const { navDispatch } = useContext(NavContext);

  const onHitClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navDispatch({ type: 'removeNavbarSelectedCategories' });
  };

  const processedAdditionalResults = useMemo(() => {
    return hits.map((hit) => {
      const processedHit = { ...hit };

      if (hit.category === 'article_category') {
        processedHit.title = `${hit.title} | ${hit.description}`;
      }

      if (hit.category === 'landing' && !hit.title) {
        processedHit.title = `${hit.meta_title}`;
      }

      return processedHit;
    });
  }, [hits]);

  return (
    <Wrapper>
      <GridWrapper>
        {processedAdditionalResults.map((hit) => (
          <HitWrapper key={hit.objectID} data-testid="nav-landing-search-result" onClick={() => onHitClick()}>
            <Hit to={hit.path}>
              <HitImageWrapper>
                <HitImage src={hit.image} alt={hit.title} />
              </HitImageWrapper>
              <HitContent>
                <HitTitle>{hit.title}</HitTitle>
              </HitContent>
            </Hit>
          </HitWrapper>
        ))}
      </GridWrapper>
    </Wrapper>
  );
}

export default NavSearchProductHits;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  button {
    margin-top: 40px;
  }
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 400px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const HitWrapper = styled.div`
  @media (max-width: ${NAV_MENU_BREAKPOINT}px) {
    width: 100%;
  }
`;

const HitImageWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 250px;

  img {
    max-height: 250px;
  }
`;

const Hit = styled(LinkTranslated)`
  cursor: pointer;
  text-decoration: none;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    text-decoration: none;
  }
`;

const HitImage = styled.img`
  width: 100%;
  border-radius: 10px;
`;

const HitContent = styled.div`
  margin-top: 16px;
`;

const HitTitle = styled.h3`
  color: ${Colors.navyBlue};
  font-family: Domaine;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.2px;
`;
